import React from "react";
import {EditElementModalForm} from "../../EditElementModalForm";
import {MultiSelect} from "../../selects/MultiSelect";
import {Spinner} from "../../Spinner";
import {FaIcon} from "../../FaIcon";
import { withTranslation } from 'react-i18next';
import {CHANNEL_INFO_BY_CHANNEL, SHEET_CHANNEL} from "../../channels/channels";

// Override de los 4 canales donde el nombre comercial difiere del de `channels.js`
// ("Facebook Ads" → "Meta Ads", "Tiktok Ads" → "TikTok Ads"). Para el resto — DV360 y SHEET
// también caen a este modal — se usa el label de `channels.js`, que los tiene a todos.
const PLATFORM_LABELS = {
    FACEBOOK: 'Meta Ads',
    GOOGLE: 'Google Ads',
    TIKTOK: 'TikTok Ads',
    LINKEDIN: 'LinkedIn Ads',
};

export class SelectItemsMultiSelectModalForm extends EditElementModalForm {

    constructor(props) {
        super(props);
        this.state = {
            items: this.getItems(),
            saving: false,
            selectedItems: [],
            userWantsToSeeInactive: false
        };

        this.onItemsChange = this.onItemsChange.bind(this);
        this.handleRefreshItemsList = this.handleRefreshItemsList.bind(this);
        this.handleRefreshClick = this.handleRefreshClick.bind(this);
        this.handleLoadInactiveClick = this.handleLoadInactiveClick.bind(this);
    }

    componentDidUpdate(prevProps) {
        // compare new prop value with previous one
        const prevItems = this.getItems(prevProps);
        const newItems = this.getItems();


        if (this.props.isLoadingInactiveItems !== prevProps.isLoadingInactiveItems || !this._ItemListsHasNoDifference(newItems, prevItems)) {
            this.setState({
                ...this.state,
                items: this.getItems()
            })
        }
    }

    _ItemListsHasNoDifference(list1, list2) {
        if (list1 === undefined && list2 === undefined)
            return true;

        if ((list1 !== undefined && list2 === undefined) || (list1 === undefined && list2 !== undefined))
            return false;

        // Sort the lists
        const sortedList1 = list1.sort((a, b) => a.id - b.id);
        const sortedList2 = list2.sort((a, b) => a.id - b.id);

        // Convert the lists to strings
        const strList1 = JSON.stringify(sortedList1);
        const strList2 = JSON.stringify(sortedList2);

        // Compare the strings
        return strList1 === strList2;
    }

    getItems(props=null){
        throw Error("Must override");
    }

    onItemsChange(data) {
        this.setState({
            selectedItems: data
        });
    }

    handleRefreshItemsList() {
        this.props.onRefreshItemsList();
    }

    handleRefreshClick(event) {
        if (event) event.preventDefault();
        if (this.props.onRefreshListItems !== undefined && !this.props.isUpdatingListItems) {
            this.props.onRefreshListItems();
        }
    }

    handleLoadInactiveClick(event) {
        if (event) event.preventDefault();
        if (this.props.onLoadInactiveItems === undefined || this.props.isLoadingInactiveItems) return;
        this.setState({userWantsToSeeInactive: true}, () => {
            this.props.onLoadInactiveItems();
        });
    }

    renderSaveButton() {
        if (this.state.saving === false) {
            return <input type="submit" value={this.addItemsButtonText()} className="btn -submit select-items-submit"/>
        } else {
            return <Spinner color="positive small"/>;
        }
    }

    handleSubmit(event) {
        event.preventDefault();
        this.setState({saving: false});
        this.props.onModified(this.state.selectedItems);

    }

    renderRefreshHeader() {
        const { t } = this.props;
        if (this.props.onRefreshListItems === undefined) return null;

        const isRefreshing = !!this.props.isUpdatingListItems;
        const lastSyncMessage = this.props.messageForRefreshListItemsIconToolbox;

        return (
            <div className="select-items-refresh-header">
                <div className="select-items-refresh-header__meta">
                    {lastSyncMessage ? <span className="select-items-refresh-header__timestamp">{lastSyncMessage}</span> : null}
                </div>
                <button
                    type="button"
                    className="select-items-refresh-header__button"
                    onClick={this.handleRefreshClick}
                    disabled={isRefreshing}
                    aria-label={t('select_items.refresh_now')}
                >
                    <FaIcon iconName="sync-alt" spin={isRefreshing}/>
                    <span>{isRefreshing ? t('select_items.refreshing') : t('select_items.refresh_now')}</span>
                </button>
            </div>
        );
    }

    platformLabel() {
        const { t } = this.props;
        const info = CHANNEL_INFO_BY_CHANNEL[this.props.channel];
        return PLATFORM_LABELS[this.props.channel]
            || (info && info.Spanish)
            || t('select_items.platform_empty_generic');
    }

    // Los valores interpolados (nombre de cuenta, label del canal) los escribe la plataforma y
    // pueden traer "/" o "&": sin escapeValue:false i18next los muestra como entidades HTML.
    // Se renderizan como texto React, así que React los sigue escapando.
    platformEmptyAccountText() {
        const { t } = this.props;
        // Sin nombre (`AdAccount._name` es nullable) la frase con "(ID …)" repetiría el mismo
        // número dos veces.
        const clave = this.props.accountName
            ? 'select_items.platform_empty_account'
            : 'select_items.platform_empty_account_no_name';
        return t(clave, {
            account: this.props.accountName,
            accountId: this.props.platformAccountId,
            interpolation: {escapeValue: false},
        });
    }

    // El reclamo va FECHADO: `hasSyncedAtLeastOnce` sólo dice que alguna vez sincronizó bien, no
    // que sea reciente — una cuenta que dejó de tener permiso hace meses conserva la fecha vieja
    // y llega hasta acá. Mostrarla es lo que evita afirmar algo atemporal sobre un dato viejo.
    platformEmptySyncText() {
        const { t } = this.props;
        const fecha = this.localizedSyncDate();
        return fecha
            ? t('select_items.platform_empty_sync', {
                date: fecha,
                interpolation: {escapeValue: false},
            })
            : t('select_items.platform_empty_sync_undated');
    }

    // `last_entities_update_on_formatted` lo arma el backend SIEMPRE en español («Hoy, 19:01»),
    // así que interpolarlo en la oración en inglés la dejaba mitad y mitad. En español se usa tal
    // cual (es el mismo texto que muestra el encabezado del modal); en otro idioma se rearma desde
    // el timestamp crudo, y si ese no vino se cae a la variante sin fecha, que dice menos pero no
    // mezcla idiomas.
    localizedSyncDate() {
        const { i18n } = this.props;
        const idioma = (i18n && i18n.language) || 'es';
        if (idioma.startsWith('es')) {
            return this.props.lastSyncFormatted || null;
        }
        if (!this.props.lastSyncISO) {
            return null;
        }
        const fecha = new Date(this.props.lastSyncISO);
        if (isNaN(fecha.getTime())) {
            return null;
        }
        return fecha.toLocaleString(idioma, {
            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
        });
    }

    renderEmptyState() {
        const { t } = this.props;
        const isRefreshing = !!this.props.isUpdatingListItems;
        const canRefresh = this.props.onRefreshListItems !== undefined;
        const inactiveAreLoaded = !!this.props.inactiveItemsAreLoaded;
        const canLoadInactive = this.props.onLoadInactiveItems !== undefined && !inactiveAreLoaded;
        const isLoadingInactive = !!this.props.isLoadingInactiveItems;
        // Sólo se afirma que la plataforma no tiene nada cuando se cumplen las tres cosas.
        // Las que protegen DE VERDAD son las dos últimas: `hasSyncedAtLeastOnce` es defensa en
        // profundidad y en la aplicación vale siempre `true` (con `last_entities_update_on` nulo,
        // `Account.accountContent()` dibuja el "sincronizando por primera vez" y este modal ni se
        // monta), y además NO distingue una sincronización de hoy de una de hace meses — por eso
        // la frase va fechada, ver `platformEmptySyncText`.
        //  - `!canLoadInactive`: hay que haber mirado activas Y pausadas; con las pausadas sin
        //    traer, el vacío todavía no prueba nada.
        //  - `accountHasNoItems`: al modal le llegan las NO seleccionadas (`getItemsFiltered`),
        //    así que con todas elegidas la lista también viene vacía, y ahí decir que la
        //    plataforma no reporta nada sería falso.
        const platformReportedNone = !!this.props.hasSyncedAtLeastOnce
            && !!this.props.platformAccountId
            && !canLoadInactive
            && !!this.props.accountHasNoItems;
        // El sólido marca la acción recomendada. Cuando el mensaje ya dijo que la plataforma
        // respondió y la cuenta está vacía, volver a sincronizar no es lo que hay que hacer:
        // va de contorno aunque sea el único botón del bloque.
        const claseDelRefresh = (canLoadInactive || platformReportedNone)
            ? 'btn -alt -negative'
            : 'btn -alt';

        return (
            <div className="select-items-empty">
                <div className="select-items-empty__icon">
                    <FaIcon iconName="inbox"/>
                </div>
                <h4 className="select-items-empty__title">
                    {platformReportedNone ? this.platformEmptyTitleText() : this.emptyTitleText()}
                </h4>
                <p className={`select-items-empty__hint${platformReportedNone ? ' select-items-empty__hint--strong' : ''}`}>
                    {platformReportedNone ? this.platformEmptyAccountText() : this.noItemsText()}
                </p>
                {platformReportedNone ? (
                    <>
                        <p className="select-items-empty__hint">{this.platformEmptySyncText()}</p>
                        <p className="select-items-empty__hint">{this.platformEmptyHintText()}</p>
                    </>
                ) : null}

                {canLoadInactive ? (
                    <>
                        <p className="select-items-empty__sync-hint">{t('select_items.empty_paused_hint')}</p>
                        <button
                            type="button"
                            className="btn -alt select-items-empty__cta"
                            onClick={this.handleLoadInactiveClick}
                            disabled={isLoadingInactive}
                        >
                            <FaIcon iconName="eye" spin={false}/>
                            <span>{isLoadingInactive ? t('select_items.loading_paused') : t('select_items.show_paused')}</span>
                        </button>
                    </>
                ) : null}

                {canRefresh ? (
                    <>
                        <p className="select-items-empty__sync-hint select-items-empty__sync-hint--secondary">
                            {/* En la rama nueva el texto de siempre ("¿Las creaste hace poco en la
                                cuenta publicitaria?") reofrece justo la explicación que el mensaje
                                de arriba viene a descartar. El botón se queda — re-sincronizar
                                confirma —, el hint no. */}
                            {platformReportedNone
                                ? t('select_items.platform_empty_refresh_hint')
                                : t('select_items.empty_sync_hint')}
                        </p>
                        <button
                            type="button"
                            className={`select-items-empty__cta ${claseDelRefresh}`}
                            onClick={this.handleRefreshClick}
                            disabled={isRefreshing}
                        >
                            <FaIcon iconName="sync-alt" spin={isRefreshing}/>
                            <span>{isRefreshing ? t('select_items.refreshing') : t('select_items.refresh_now')}</span>
                        </button>
                    </>
                ) : null}
            </div>
        );
    }

    renderFormFields() {
        const items = this.getItems();
        const onLoadInactiveItems = this.props.onLoadInactiveItems !== undefined ? this.props.onLoadInactiveItems : null;

        if (items === null) {
            return (
                <div className="box-loader">
                    <Spinner color="positive"/>
                </div>
            );
        }

        return (
            <>
                {this.renderRefreshHeader()}
                {items.length > 0 ? (
                    <div className="form-row select-items-list">
                        <MultiSelect
                            longList={true}
                            showOnlyActiveInitialStatus={!this.state.userWantsToSeeInactive}
                            items={items}
                            selectedItemIds={this.state.selectedItems}
                            onChange={this.onItemsChange}
                            showOnlyActiveCheck={true}
                            isUpdatingListItems={this.props.isUpdatingListItems}
                            onLoadInactiveItems={onLoadInactiveItems}
                            inactiveItemsAreLoaded={this.props.inactiveItemsAreLoaded}
                            isLoadingInactiveItems={this.props.isLoadingInactiveItems}
                        />
                    </div>
                ) : this.renderEmptyState()}
            </>
        );
    }

    noItemsText() {
        throw Error("Must override");
    }

    emptyTitleText() {
        throw Error("Must override");
    }

    platformEmptyTitleText() {
        throw Error("Must override");
    }

    platformEmptyHintText() {
        throw Error("Must override");
    }

    addItemsButtonText() {
        throw Error("Must override");
    }

}

class SelectCampaignsMultiSelectModalFormComponent extends SelectItemsMultiSelectModalForm {
    noItemsText() {
        const { t } = this.props;
        return t('select_items.no_campaigns');
    }

    emptyTitleText() {
        const { t } = this.props;
        return t('select_items.empty_title_campaigns');
    }

    platformEmptyTitleText() {
        const { t } = this.props;
        return t('select_items.platform_empty_title_campaigns', {
            platform: this.platformLabel(),
            interpolation: {escapeValue: false},
        });
    }

    platformEmptyHintText() {
        const { t } = this.props;
        // Una planilla no tiene "cuenta publicitaria" que cambiar: tiene un archivo que revisar.
        return this.props.channel === SHEET_CHANNEL
            ? t('select_items.platform_empty_hint_sheet')
            : t('select_items.platform_empty_hint_campaigns');
    }

    addItemsButtonText() {
        const { t } = this.props;
        return t('select_items.add_campaigns');
    }

    getItems(props=null){
        return props === null ? this.props.campaigns : props.campaigns;
    }
}

class SelectAdgroupsMultiSelectModalFormComponent extends SelectItemsMultiSelectModalForm {
    noItemsText() {
        const { t } = this.props;
        return t('select_items.no_adgroups');
    }

    emptyTitleText() {
        const { t } = this.props;
        return t('select_items.empty_title_adgroups');
    }

    platformEmptyTitleText() {
        const { t } = this.props;
        return t('select_items.platform_empty_title_adgroups', {
            platform: this.platformLabel(),
            interpolation: {escapeValue: false},
        });
    }

    platformEmptyHintText() {
        const { t } = this.props;
        return this.props.channel === SHEET_CHANNEL
            ? t('select_items.platform_empty_hint_sheet')
            : t('select_items.platform_empty_hint_adgroups');
    }

    addItemsButtonText() {
        const { t } = this.props;
        return t('select_items.add_adgroups');
    }

    getItems(props=null){
        props = props === null ? this.props : props;

        return props.adgroups.map(adgroup => {
            const item = {...adgroup};
            item.name = `${adgroup.campaign_name} ➡️ ${adgroup.name}`;
            return item;
        });
    }
}

export const SelectCampaignsMultiSelectModalForm = withTranslation()(SelectCampaignsMultiSelectModalFormComponent);
export const SelectAdgroupsMultiSelectModalForm = withTranslation()(SelectAdgroupsMultiSelectModalFormComponent);